import type {
  GapExecutionScopeSection,
  GapReviewSalesScopeDetailsView,
} from '@momentum/types';
import {
  GapAnalysisConsistencyStatus,
  GapAnalysisEligibilityStatus,
} from '@momentum/types';

import { pluralize } from '../lib/format';
import { verdictTone } from '../lib/run-audit';
import { Prose } from './Prose';

const ELIGIBILITY_TONE: Record<GapAnalysisEligibilityStatus, string> = {
  [GapAnalysisEligibilityStatus.ELIGIBLE]: 'emerald',
  [GapAnalysisEligibilityStatus.INELIGIBLE]: 'rose',
  [GapAnalysisEligibilityStatus.UNRESOLVED]: 'amber',
};

const CONSISTENCY_TONE: Record<GapAnalysisConsistencyStatus, string> = {
  [GapAnalysisConsistencyStatus.COMPLETED]: 'emerald',
  [GapAnalysisConsistencyStatus.PENDING]: 'amber',
  [GapAnalysisConsistencyStatus.FAILED]: 'rose',
  [GapAnalysisConsistencyStatus.NOT_REQUIRED]: '',
};

const CONSISTENCY_DETAIL: Record<GapAnalysisConsistencyStatus, string> = {
  [GapAnalysisConsistencyStatus.COMPLETED]:
    'Verdicts were reconciled across every validator batch on this deal.',
  [GapAnalysisConsistencyStatus.PENDING]:
    'The deal-wide pass has not run, so batch verdicts stand unreconciled.',
  [GapAnalysisConsistencyStatus.FAILED]:
    'The pass failed. Every gap it covered stays pending, and the superseded batch verdicts below are the only adjudication that exists.',
  [GapAnalysisConsistencyStatus.NOT_REQUIRED]:
    'Zero or one usable batch — there was nothing to reconcile.',
};

interface SalesScopeProps {
  scopeName: string;
  sales: GapReviewSalesScopeDetailsView;
}

function SalesScope({ scopeName, sales }: SalesScopeProps) {
  return (
    <div className="gr-cols2">
      <div className="gr-stack">
        <div className="gr-card">
          <div className="gr-kv">
            <span>scope</span>
            <span>{scopeName}</span>
          </div>
          <div className="gr-kv">
            <span>eligibility.status</span>
            <span>{sales.eligibility.status}</span>
          </div>
          <div className="gr-kv">
            <span>suppressedCandidateCount</span>
            <span>{sales.suppressedCandidateCount}</span>
          </div>
          <div className="gr-kv">
            <span>consistencyStatus</span>
            <span>{sales.consistencyStatus}</span>
          </div>
        </div>

        <div className="gr-card">
          <span className="gr-card-k">eligibility.rationale</span>
          <div className="gr-card-v">
            <Prose text={sales.eligibility.rationale} />
          </div>
        </div>

        {/*
          Both finder families' readings are kept whether or not they agreed: a
          disagreement is reported as a data-quality observation, never resolved
          silently. The stored notes carry no family label, so they are numbered
          in the order the pipeline wrote them rather than attributed.
        */}
        <div className="gr-card">
          <span className="gr-card-k">
            eligibilityNotes · {sales.eligibilityNotes.length}
          </span>
          <div className="gr-card-v">
            {sales.eligibilityNotes.map((note, noteIndex) => (
              <div key={`${noteIndex}-${note.rationale.slice(0, 32)}`}>
                <span
                  className="gr-chip gr-stack-top-sm"
                  data-tone={ELIGIBILITY_TONE[note.status]}
                >
                  reading {noteIndex + 1} · {note.status}
                </span>
                <Prose text={note.rationale} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="gr-stack">
        <div
          className="gr-passrow"
          data-tone={CONSISTENCY_TONE[sales.consistencyStatus]}
        >
          <div>
            <div className="gr-passrow-t">Deal-wide consistency pass</div>
            <div className="gr-passrow-s">
              {CONSISTENCY_DETAIL[sales.consistencyStatus]}
            </div>
          </div>
          <span
            className="gr-chip"
            data-tone={CONSISTENCY_TONE[sales.consistencyStatus]}
          >
            {sales.consistencyStatus}
          </span>
        </div>

        <div className="gr-card">
          <span className="gr-card-k">procurementPathNotes</span>
          {sales.procurementPathNotes.map((note) => (
            <div className="gr-kv" key={note.finderFamily}>
              <span>{note.finderFamily}</span>
              <span>{note.procurementPath}</span>
            </div>
          ))}
        </div>

        <div>
          <span className="gr-card-k">
            supersededValidations · {sales.supersededValidations.length}
          </span>
          <p className="gr-cand-claim gr-stack-top-sm">
            The only place in the schema where a verdict flip is recorded. Each
            card is the batch validator&apos;s verdict that the deal-wide pass
            replaced — not the verdict the gap ended up with, which is the one
            the reader and the validator tab show.
          </p>
        </div>

        {sales.supersededValidations.length === 0 ? (
          <div className="gr-empty">
            <b>No verdict was flipped</b>
            Every batch verdict on this deal survived the deal-wide pass.
          </div>
        ) : (
          sales.supersededValidations.map((superseded) => (
            <div className="gr-cand" key={superseded.gapId}>
              <div className="gr-cand-top">
                <span className="gr-chip gr-chip-lg">{superseded.gapId}</span>
                <span
                  className="gr-chip"
                  data-tone={verdictTone(superseded.validation.verdict)}
                >
                  was {superseded.validation.verdict}
                </span>
              </div>
              <span className="gr-cand-k">superseded headline</span>
              <p className="gr-cand-claim">{superseded.validation.headline}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * The sales pipeline's extra per-deal bookkeeping.
 *
 * Delivery scopes never carry it, so the section renders only for the scopes
 * that have it — a sales execution's scopes all do, and a delivery execution
 * shows nothing rather than an empty section.
 */
export function SalesCoverageSection({
  scopes,
}: {
  scopes: GapExecutionScopeSection[];
}) {
  const salesScopes = scopes.flatMap((scope) =>
    scope.coverage.sales === undefined
      ? []
      : [{ scopeName: scope.scopeName, sales: scope.coverage.sales }]
  );

  if (salesScopes.length === 0) return null;

  return (
    <section>
      <div className="gr-rule gr-rule-flush">
        <span>Sales coverage · {pluralize(salesScopes.length, 'deal')}</span>
        <i />
      </div>

      <div className="gr-stack-lg">
        {salesScopes.map((salesScope) => (
          <SalesScope
            key={salesScope.scopeName}
            scopeName={salesScope.scopeName}
            sales={salesScope.sales}
          />
        ))}
      </div>
    </section>
  );
}
